import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Calculator as CalcIcon, Plus, Save, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, dataBR, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ErrorState, Field, Loading, PageHeader, Surface, Thumb } from "@/components/ds";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { CostHistoryPanel, CostHistoryToggle } from "@/components/products/CostHistoryPanel";

const QUANTITIES = [1, 10, 50, 100];
const CHANNELS = ["VENDA DIRETA", "CONSIGNADO", "ATACADO"];

const Line = ({ label, value, strong, testid }) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <span className={strong ? "" : "text-muted-foreground"}>{label}</span>
    <span className={`font-num ${strong ? "text-base" : ""}`} data-testid={testid}>
      {value}
    </span>
  </div>
);

export default function Calculator() {
  const [params] = useSearchParams();
  const [ctx, setCtx] = useState(null);
  const [products, setProducts] = useState([]);
  const [kits, setKits] = useState([]);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [plateOptions, setPlateOptions] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [plateId, setPlateId] = useState("");

  const [target, setTarget] = useState(params.get("product") ? `p:${params.get("product")}` : "");
  const [printerId, setPrinterId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [wasteMode, setWasteMode] = useState("AUTO");
  const [wastePercent, setWastePercent] = useState("5");
  const [extras, setExtras] = useState([]);
  const [markup, setMarkup] = useState("100");
  const [margin, setMargin] = useState("60");
  const [prices, setPrices] = useState({});
  const [result, setResult] = useState(null);
  const [priceEdit, setPriceEdit] = useState({ markup: "", margin: "" });
  const [commercial, setCommercial] = useState({ consign: "", reseller: "" });
  const [busy, setBusy] = useState(false);
  const [variationId, setVariationId] = useState("");
  const [componentBase, setComponentBase] = useState("");

  useEffect(() => {
    Promise.all([api.get("/pricing/context"), api.get("/products"), api.get("/kits"),
                 api.get("/pricing/calculations")])
      .then(([c, p, k, h]) => {
        setCtx(c.data);
        setProducts(p.data);
        setKits(k.data);
        setHistory(h.data);
        const pref = c.data.printers.find((x) => x.preferred) || c.data.printers[0];
        if (pref) setPrinterId(pref.id);
      })
      .catch((e) => setError(apiError(e)));
  }, []);

  const body = useCallback(() => {
    const [kind, id] = (target || "").split(":");
    return {
      product_id: kind === "p" ? id : null,
      variation_id: kind === "p" && variationId ? variationId : null,
      kit_id: kind === "k" ? id : null,
      plate_id: kind === "p" && plateId ? plateId : null,
      printer_id: printerId || null,
      quantity: Number(quantity) || 1,
      component_base: componentBase ? Number(componentBase) : null,
      waste_mode: wasteMode,
      waste_percent: Number(wastePercent || 0),
      extras: extras
        .filter((e) => e.name)
        .map((e) => ({
          name: e.name,
          quantity: Number(e.quantity || 0),
          unit_cost: Number(e.unit_cost || 0),
          kind: e.kind,
        })),
      markup_percent: markup === "" ? null : Number(markup),
      margin_percent: margin === "" ? null : Number(margin),
      channel_prices: Object.fromEntries(
        Object.entries(prices).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)])
      ),
    };
  }, [target, printerId, quantity, wasteMode, wastePercent, extras, markup, margin, prices, plateId,
      variationId, componentBase]);

  useEffect(() => {
    setVariationId("");
    setComponentBase("");
  }, [target]);

  useEffect(() => {
    const [kind, id] = (target || "").split(":");
    if (kind !== "p" || !id) {
      setPlateOptions(null);
      setPlateId("");
      return;
    }
    api
      .get(`/products/${id}/plate-options`, {
        params: {
          quantity: Number(quantity) || 1,
          variation_id: variationId || undefined,
          component_base: componentBase || undefined,
        },
      })
      .then(({ data }) => {
        setPlateOptions(data);
        setPlateId((cur) => {
          const valid = data.options.some((o) => o.plate_id === cur);
          return valid ? cur : data.recommended_plate_id || "";
        });
        // Capacidades diferentes: a base fica explícita para o operador.
        if (data.component_mixed && !componentBase && data.component_base) {
          setComponentBase(String(data.component_base));
        }
      })
      .catch(() => setPlateOptions(null));
  }, [target, quantity, variationId, componentBase]);

  const calculate = async () => {
    if (!target) {
      toast.error("Escolha um produto ou kit.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/pricing/calculate", body());
      setResult(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    try {
      await api.post("/pricing/calculations", body());
      const { data } = await api.get("/pricing/calculations");
      setHistory(data);
      toast.success("Cálculo salvo no histórico.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Preenche os campos editáveis sempre que um novo cálculo chega (sem recalcular ao digitar).
  useEffect(() => {
    setPriceEdit({
      markup: result?.pricing?.markup?.price ? String(result.pricing.markup.price) : "",
      margin: result?.pricing?.margin?.price ? String(result.pricing.margin.price) : "",
    });
  }, [result]);

  const num = (raw) => Number(String(raw ?? "").replace(",", "."));

  const applyPrice = async (raw) => {
    const price = num(raw);
    if (!price || price <= 0) {
      toast.error("Calcule um custo válido antes de aplicar o preço (o produto precisa de Plate).");
      return;
    }
    const [kind, id] = target.split(":");
    try {
      await api.post("/pricing/apply", {
        product_id: kind === "p" ? id : null,
        kit_id: kind === "k" ? id : null,
        price,
        cost: result?.costs?.unit,
        consign_price: num(commercial.consign) > 0 ? num(commercial.consign) : undefined,
        reseller_price: num(commercial.reseller) > 0 ? num(commercial.reseller) : undefined,
      });
      toast.success(`Preço de ${brl(price)} aplicado ao ${kind === "k" ? "kit" : "produto"}.`);
      const { data } = await api.get("/products");
      setProducts(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (error) return <ErrorState message={error} onRetry={() => window.location.reload()} />;
  if (!ctx) return <Loading label="Abrindo a calculadora..." />;

  const [targetKind, targetId] = (target || "").split(":");
  const selectedProduct = targetKind === "p" ? products.find((p) => p.id === targetId) : null;
  const variations = (selectedProduct?.variations || []).filter((v) => v.active !== false);

  const options = [
    { value: "", label: "Escolha o produto ou kit" },
    ...products.map((p) => ({ value: `p:${p.id}`, label: `${p.code || ""} ${p.name}`.trim() })),
    ...kits.map((k) => ({ value: `k:${k.id}`, label: `KIT · ${k.name}` })),
  ];
  const c = result?.costs;

  return (
    <div className="space-y-8" data-testid="calculator-page">
      <PageHeader
        title="Calculadora de Preço"
        subtitle="Usa suas Plates, filamentos, produções e impressoras reais para mostrar o custo de verdade e sugerir o preço."
        action={<CostHistoryToggle open={historyOpen} onToggle={() => setHistoryOpen((v) => !v)} />}
      />

      {historyOpen && <CostHistoryPanel products={products} />}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Surface className="min-w-0 space-y-4 p-5" data-testid="calculator-form">
          <Field label="Produto ou kit">
            <NativeSelect value={target} onChange={setTarget} options={options} data-testid="calc-target-select" />
          </Field>
          {variations.length > 0 && (
            <Field
              label="Cor / combinação"
              hint="As cores entram nos filamentos da Plate pela ordem de consumo"
            >
              <NativeSelect
                value={variationId}
                onChange={setVariationId}
                placeholder="Filamento padrão da Plate"
                data-testid="calc-variation-select"
                options={variations.map((v) => ({
                  value: v.id,
                  label: `${v.name}${v.filament_label ? ` · ${v.filament_label}` : " · sem filamento"}`,
                }))}
              />
            </Field>
          )}
          {plateOptions && plateOptions.options.length > 0 && (
            <Field
              label="Plate deste produto"
              hint="Filamentos, gramas, unidades por execução e tempo vêm da Plate escolhida"
            >
              <NativeSelect
                value={plateId}
                onChange={setPlateId}
                data-testid="calc-plate-select"
                options={plateOptions.options.map((o) => ({
                  value: o.plate_id,
                  label: `${o.plate_name} · ${o.output_quantity} un · ${Math.round(o.print_time_min)} min${
                    o.recommended ? " (recomendada)" : ""
                  }`,
                }))}
              />
            </Field>
          )}
          {plateOptions?.component_mixed && (
            <div
              className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3"
              data-testid="calc-component-base"
            >
              <p className="flex items-center gap-2 text-xs text-amber-200">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                Plates com quantidades diferentes por execução.
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">
                Escolha a base de cálculo: só as Plates daquele grupo entram na conta.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {plateOptions.component_groups.map((g) => (
                  <button
                    key={g.output_quantity}
                    type="button"
                    onClick={() => setComponentBase(String(g.output_quantity))}
                    data-testid={`calc-component-base-${g.output_quantity}`}
                    className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                      Number(componentBase) === g.output_quantity
                        ? "border-brand bg-brand/20 text-foreground"
                        : "border-white/10 text-muted-foreground hover:border-white/30"
                    }`}
                  >
                    {g.output_quantity} unidades · {g.plates} plate{g.plates > 1 ? "s" : ""}
                  </button>
                ))}
              </div>
            </div>
          )}
          {plateOptions?.component_plan?.length > 0 && (
            <div
              className="rounded-lg border border-brand/30 bg-brand/5 p-3"
              data-testid="calc-component-plan"
            >
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                {plateOptions.component_mixed
                  ? `Base de ${plateOptions.component_base} un · ${plateOptions.component_plan.length} peças neste grupo`
                  : `Produto montado com ${plateOptions.component_plan.length} peças · o cálculo soma todas`}
              </p>
              <ul className="mt-2 space-y-1">
                {plateOptions.component_plan.map((c) => (
                  <li
                    key={c.plate_id}
                    className="flex items-center justify-between gap-3 text-xs"
                    data-testid={`calc-component-plan-${c.plate_id}`}
                  >
                    <span className="min-w-0 truncate">
                      {c.plate_name} · {c.runs}x execução ({c.output_quantity} un cada)
                    </span>
                    <span className="shrink-0 font-num">
                      {Math.round(c.total_time_min)} min · {brl(c.estimated_cost)}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 border-t border-white/10 pt-2 font-num text-xs">
                Total estimado (peças + materiais):{" "}
                {Math.round(plateOptions.component_plan_totals.total_time_min)} min ·{" "}
                {brl(plateOptions.component_plan_totals.estimated_cost)}
              </p>
            </div>
          )}
          {plateOptions && plateOptions.options.length === 0 &&
            !plateOptions.component_plan?.length && (
            <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="calc-no-plate">
              Este produto ainda não tem Plate cadastrada.
            </p>
          )}
          {plateOptions && plateOptions.materials.length > 0 && (
            <div className="rounded-lg border border-white/10 bg-secondary/40 p-3" data-testid="calc-product-materials">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Materiais do produto (do estoque)
              </p>
              <ul className="mt-2 space-y-1">
                {plateOptions.materials.map((m) => (
                  <li key={m.material_id} className="flex items-center justify-between gap-3 text-xs">
                    <span className="truncate">
                      {m.quantity_per_unit}x {m.name}
                    </span>
                    <span className="font-num">
                      {m.needed} {m.unit} · {brl(m.cost)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Field label="Impressora" hint="Potência e custo/hora vêm de Configurações > Impressoras">
            <NativeSelect
              value={printerId}
              onChange={setPrinterId}
              options={[
                { value: "", label: ctx.printers.length ? "Preferencial" : "Nenhuma cadastrada" },
                ...ctx.printers.map((p) => ({
                  value: p.id,
                  label: `${p.name} · ${p.power_watts}W · ${brl(p.cost_per_hour)}/h`,
                })),
              ]}
              data-testid="calc-printer-select"
            />
          </Field>
          <div>
            <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Quantidade</p>
            <div className="flex flex-wrap gap-2">
              {QUANTITIES.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => setQuantity(q)}
                  data-testid={`calc-qty-${q}`}
                  className={`rounded-full border px-3.5 py-1.5 font-num text-xs ${
                    Number(quantity) === q
                      ? "border-brand/50 bg-brand/15 text-brand"
                      : "border-white/10 text-muted-foreground"
                  }`}
                >
                  {q} un
                </button>
              ))}
              <Input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="h-9 w-24 font-num"
                data-testid="calc-qty-input"
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Refugo">
              <NativeSelect
                value={wasteMode}
                onChange={setWasteMode}
                options={[
                  { value: "AUTO", label: "Automático (usa o histórico)" },
                  { value: "HISTORICO", label: "Só histórico real" },
                  { value: "ESTIMADO", label: "Estimado por mim" },
                ]}
                data-testid="calc-waste-mode-select"
              />
            </Field>
            <Field label="Refugo estimado (%)">
              <Input
                type="number"
                min="0"
                value={wastePercent}
                onChange={(e) => setWastePercent(e.target.value)}
                className="font-num"
                data-testid="calc-waste-percent-input"
              />
            </Field>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Materiais e embalagem
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() =>
                  setExtras((s) => [...s, { name: "", quantity: 1, unit_cost: "", kind: "MATERIAL" }])
                }
                data-testid="add-extra-btn"
                className="h-8 gap-1.5 rounded-full text-[11px]"
              >
                <Plus className="h-3 w-3" /> Adicionar
              </Button>
            </div>
            <div className="space-y-2">
              {extras.map((e, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto] items-center gap-2 rounded-lg border border-white/10 bg-secondary/40 p-2" data-testid={`extra-row-${i}`}>
                  <Input
                    placeholder="Ex.: argola"
                    value={e.name}
                    onChange={(ev) =>
                      setExtras((s) => s.map((x, j) => (j === i ? { ...x, name: ev.target.value } : x)))
                    }
                    className="col-span-2 h-9 min-w-0"
                    data-testid={`extra-name-${i}`}
                  />
                  <div className="flex min-w-0 items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    value={e.quantity}
                    onChange={(ev) =>
                      setExtras((s) => s.map((x, j) => (j === i ? { ...x, quantity: ev.target.value } : x)))
                    }
                    className="h-9 w-16 font-num"
                    data-testid={`extra-qty-${i}`}
                  />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="R$"
                    value={e.unit_cost}
                    onChange={(ev) =>
                      setExtras((s) => s.map((x, j) => (j === i ? { ...x, unit_cost: ev.target.value } : x)))
                    }
                    className="h-9 w-20 font-num"
                    data-testid={`extra-cost-${i}`}
                  />
                  <NativeSelect
                    value={e.kind}
                    onChange={(v) => setExtras((s) => s.map((x, j) => (j === i ? { ...x, kind: v } : x)))}
                    options={[
                      { value: "MATERIAL", label: "Material" },
                      { value: "EMBALAGEM", label: "Embalagem" },
                    ]}
                    className="h-9 min-w-0 flex-1"
                    data-testid={`extra-kind-${i}`}
                  />
                  </div>
                  <button
                    type="button"
                    onClick={() => setExtras((s) => s.filter((_, j) => j !== i))}
                    data-testid={`remove-extra-${i}`}
                    className="p-2 text-muted-foreground"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Markup (%)" hint="Markup de 100% não é margem de 100%">
              <Input type="number" value={markup} onChange={(e) => setMarkup(e.target.value)} className="font-num" data-testid="calc-markup-input" />
            </Field>
            <Field label="Margem desejada (%)">
              <Input type="number" value={margin} onChange={(e) => setMargin(e.target.value)} className="font-num" data-testid="calc-margin-input" />
            </Field>
          </div>

          <div>
            <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
              Preços por canal (simulação)
            </p>
            <div className="grid gap-2 sm:grid-cols-3">
              {CHANNELS.map((name) => (
                <Input
                  key={name}
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={name}
                  value={prices[name] ?? ""}
                  onChange={(e) => setPrices((s) => ({ ...s, [name]: e.target.value }))}
                  className="h-9 font-num"
                  data-testid={`calc-price-${name.replace(/\s/g, "-").toLowerCase()}`}
                />
              ))}
            </div>
          </div>

          <Button
            onClick={calculate}
            disabled={busy}
            data-testid="calculate-btn"
            className="h-11 w-full gap-2 rounded-full font-semibold transition-transform active:scale-95"
          >
            <CalcIcon className="h-4 w-4" /> {busy ? "Calculando..." : "Calcular"}
          </Button>
          <p className="text-[11px] text-muted-foreground">
            É só simulação: nada muda no produto até você clicar em “Aplicar ao produto”.
          </p>
        </Surface>

        {result ? (
          <div className="min-w-0 space-y-4" data-testid="calculator-result">
            <Surface className="min-w-0 overflow-hidden p-5">
              <div className="flex items-center gap-3">
                <Thumb src={fileUrl(result.target.photo_path)} alt={result.target.name} />
                <div className="min-w-0">
                  <p className="truncate font-display text-base font-medium">{result.target.name}</p>
                  <p className="truncate font-num text-[11px] text-muted-foreground">
                    {result.target.code} · {result.quantity} un · {Math.round(result.time_min)} min
                    {result.printer ? ` · ${result.printer.name}` : ""}
                  </p>
                </div>
              </div>

              {result.missing_plate && (
                <p className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-red-300" data-testid="calc-missing-plate">
                  Este produto ainda não tem Plate cadastrada, então não há como calcular o
                  filamento e o tempo.
                </p>
              )}
              {result.unpriced_filaments?.length > 0 && (
                <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid="calc-unpriced-filaments">
                  Sem valor de compra para: {result.unpriced_filaments.join(", ")}. Registre a
                  entrada do filamento com o valor pago para o custo ficar exato.
                </p>
              )}

              <div className="mt-4 space-y-1.5 border-t border-white/10 pt-4">
                <Line label="Filamento" value={brl(c.material)} testid="cost-material" />
                <Line label="Energia" value={brl(c.energy)} testid="cost-energy" />
                <Line label="Máquina" value={brl(c.machine)} testid="cost-machine" />
                <Line label="Materiais do produto" value={brl(c.supplies || 0)} testid="cost-supplies" />
                <Line label="Materiais adicionais" value={brl(c.extras)} testid="cost-extras" />
                <Line label="Embalagem" value={brl(c.packaging)} testid="cost-packaging" />
                <Line
                  label={`Refugo (${result.waste.percent}% · ${
                    result.waste.mode === "HISTORICO" ? "histórico real" : "estimado"
                  })`}
                  value={brl(c.waste)}
                  testid="cost-waste"
                />
                <div className="mt-2 border-t border-white/10 pt-2">
                  <Line label="Custo total" value={brl(c.total)} strong testid="cost-total" />
                  <Line label="Custo por unidade" value={brl(c.unit)} strong testid="cost-unit" />
                </div>
              </div>
              {result.waste.mode === "HISTORICO" && (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Refugo calculado com {result.waste.samples} produção(ões) reais.
                </p>
              )}
              {result.real_cost?.available && (
                <p className="mt-2 rounded-lg border border-white/10 bg-secondary/40 p-3 text-xs" data-testid="calc-real-cost">
                  Custo real médio das produções: {brl(result.real_cost.avg_unit_cost)} por unidade
                  ({result.real_cost.samples} produção(ões)) · diferença{" "}
                  {brl(result.real_cost.avg_unit_cost - c.unit)}
                </p>
              )}
            </Surface>

            {result.kit_items?.length > 0 && (
              <Surface className="p-5" data-testid="calc-kit-items">
                <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                  Itens do kit
                </p>
                {result.kit_items.map((k) => (
                  <div key={k.product_id} className="flex items-center justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate">
                      {k.quantity_per_kit}x {k.name}
                      {!k.has_plate && (
                        <span className="ml-1 text-[10px] text-amber-300">sem Plate</span>
                      )}
                    </span>
                    <span className="shrink-0 font-num text-xs">{brl(k.cost)}</span>
                  </div>
                ))}
                {result.items_without_plate?.length > 0 && (
                  <p className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid="calc-kit-missing-plate">
                    Sem Plate cadastrada para: {result.items_without_plate.join(", ")}. O custo
                    desses itens está saindo como R$ 0,00.
                  </p>
                )}
              </Surface>
            )}

            {result.blocks?.length > 1 && (
              <Surface className="p-5" data-testid="calc-components">
                <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">
                  Componentes / Plates
                </p>
                {result.blocks.map((b, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate">
                      {b.plate_name} · {b.runs}x
                    </span>
                    <span className="shrink-0 font-num text-xs">
                      {brl(b.material + b.energy + b.machine)}
                    </span>
                  </div>
                ))}
              </Surface>
            )}

            <Surface className="space-y-3 p-5" data-testid="calc-prices">
              <div className="grid gap-3 sm:grid-cols-2" data-testid="calc-commercial-prices">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Preço consignado (R$)
                  </span>
                  <Input
                    value={commercial.consign}
                    onChange={(e) => setCommercial((c) => ({ ...c, consign: e.target.value }))}
                    inputMode="decimal"
                    placeholder="não alterar"
                    data-testid="calc-consign-price-input"
                    className="h-9 font-num text-sm"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Preço lojista (R$)
                  </span>
                  <Input
                    value={commercial.reseller}
                    onChange={(e) => setCommercial((c) => ({ ...c, reseller: e.target.value }))}
                    inputMode="decimal"
                    placeholder="não alterar"
                    data-testid="calc-reseller-price-input"
                    className="h-9 font-num text-sm"
                  />
                </label>
                <p className="text-[11px] text-muted-foreground sm:col-span-2">
                  Preenchidos, eles são salvos junto quando você clica em Aplicar. Só o preço
                  público aparece no catálogo.
                </p>
              </div>
              {result.pricing.markup && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground">
                    Markup {result.pricing.markup.percent}% → margem{" "}
                    {result.pricing.markup.margin_percent}%
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="font-num text-[11px] text-muted-foreground" data-testid="markup-price">
                      calculado {brl(result.pricing.markup.price)}
                    </span>
                    <Input
                      value={priceEdit.markup}
                      onChange={(e) => setPriceEdit((p) => ({ ...p, markup: e.target.value }))}
                      inputMode="decimal"
                      aria-label="Preço para aplicar (markup)"
                      data-testid="markup-price-input"
                      className="h-8 w-24 rounded-full text-center font-num text-sm"
                    />
                    <Button size="sm" variant="outline" disabled={!priceEdit.markup} onClick={() => applyPrice(priceEdit.markup)} data-testid="apply-markup-btn" className="h-8 rounded-full text-[11px]">

                      Aplicar
                    </Button>
                  </span>
                </div>
              )}
              {result.pricing.margin && (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground">
                    Margem {result.pricing.margin.percent}% · lucro{" "}
                    {brl(result.pricing.margin.profit)}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="font-num text-[11px] text-muted-foreground" data-testid="margin-price">
                      calculado {brl(result.pricing.margin.price)}
                    </span>
                    <Input
                      value={priceEdit.margin}
                      onChange={(e) => setPriceEdit((p) => ({ ...p, margin: e.target.value }))}
                      inputMode="decimal"
                      aria-label="Preço para aplicar (margem)"
                      data-testid="margin-price-input"
                      className="h-8 w-24 rounded-full text-center font-num text-sm"
                    />
                    <Button size="sm" variant="outline" disabled={!priceEdit.margin} onClick={() => applyPrice(priceEdit.margin)} data-testid="apply-margin-btn" className="h-8 rounded-full text-[11px]">
                      Aplicar
                    </Button>
                  </span>
                </div>
              )}
              {result.pricing.channels.map((ch) => (
                <div
                  key={ch.name}
                  className={`rounded-lg border p-3 ${
                    ch.below_cost ? "border-destructive/40 bg-destructive/10" : "border-white/10 bg-secondary/40"
                  }`}
                  data-testid={`channel-${ch.name.replace(/\s/g, "-").toLowerCase()}`}
                >
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="min-w-0 truncate">{ch.name}</span>
                    <span className="shrink-0 font-num">{brl(ch.price)}</span>
                  </div>
                  {ch.below_cost ? (
                    <p className="mt-1 flex items-start gap-1.5 text-[11px] text-red-300">
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                      Este preço está abaixo do custo. Prejuízo de {brl(ch.loss_per_unit)} por
                      unidade.
                    </p>
                  ) : (
                    <p className="mt-1 font-num text-[11px] text-muted-foreground">
                      lucro {brl(ch.profit)}/un · margem {ch.margin_percent}% · total{" "}
                      {brl(ch.total_profit)}
                    </p>
                  )}
                </div>
              ))}
              {result.target.consign_prices?.length > 0 && (
                <div className="border-t border-white/10 pt-3" data-testid="calc-consign">
                  <p className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">
                    Consignado cadastrado
                  </p>
                  {result.target.consign_prices.map((p) => (
                    <div key={p.partner_name} className="flex items-center justify-between gap-3 text-xs">
                      <span className="min-w-0 truncate">{p.partner_name}</span>
                      <span className="shrink-0 font-num">
                        {brl(p.price)} · lucro {brl(p.price - c.unit)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2 border-t border-white/10 pt-3">
                <Button variant="outline" onClick={save} data-testid="save-calculation-btn" className="h-10 gap-2 rounded-full text-xs">
                  <Save className="h-3.5 w-3.5" /> Salvar cálculo
                </Button>
                <Button variant="outline" onClick={calculate} data-testid="recalculate-btn" className="h-10 gap-2 rounded-full text-xs">
                  <Wand2 className="h-3.5 w-3.5" /> Recalcular
                </Button>
              </div>
            </Surface>
          </div>
        ) : (
          <Surface className="flex min-h-[240px] items-center justify-center p-8 text-center">
            <p className="text-sm text-muted-foreground">
              Escolha um produto e clique em Calcular para ver o custo real e o preço sugerido.
            </p>
          </Surface>
        )}
      </div>

      {history.length > 0 && (
        <Surface className="p-5" data-testid="calc-history">
          <p className="mb-3 text-xs uppercase tracking-wide text-muted-foreground">
            Cálculos salvos
          </p>
          <ul className="divide-y divide-white/10">
            {history.map((h) => (
              <li key={h.id} className="flex min-w-0 items-center gap-3 py-2.5 text-sm">
                <span className="w-14 shrink-0 font-num text-xs text-muted-foreground">
                  {dataBR(h.created_at)}
                </span>
                <span className="min-w-0 flex-1 truncate">{h.name}</span>
                <span className="shrink-0 font-num text-xs">
                  {h.quantity} un · custo {brl(h.unit_cost)}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await api.delete(`/pricing/calculations/${h.id}`);
                      setHistory((s) => s.filter((x) => x.id !== h.id));
                      toast.success("Cálculo removido do histórico.");
                    } catch (e) {
                      toast.error(apiError(e));
                    }
                  }}
                  data-testid={`delete-calculation-btn-${h.id}`}
                  className="shrink-0 p-1.5 text-muted-foreground transition-colors hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </Surface>
      )}
    </div>
  );
}
